import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";
import { scrollToId } from "./scrollUtils";
import { PrivacyDialog } from "./PrivacyDialog";
import { CookiePolicyDialog } from "./CookiePolicyDialog";
import { CONSENT_KEY } from "./CookieBanner";

const WHATSAPP = "393473275955";
const EMAIL = "info@alinacossali.it";

export const Footer = () => {
  const scrollToHero = () => scrollToId("#hero");

  return (
    <footer
      className="relative text-crema pt-24 sm:pt-32 pb-10 bg-gradient-to-b from-espresso/80 via-espresso/90 to-espresso/95 backdrop-blur-md"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-fuchsiabrand mb-8">
          Iniziamo insieme
        </p>

        <motion.a
          href={`mailto:${EMAIL}`}
          data-testid="footer-email"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="group inline-flex items-center gap-2.5 sm:gap-3 font-display text-xl sm:text-3xl lg:text-5xl leading-tight tracking-tight hover:text-fuchsiabrand transition-colors duration-300"
        >
          <span className="break-words">{EMAIL}</span>
          <ArrowUpRight className="flex-shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300 w-6 h-6 sm:w-9 sm:h-9 lg:w-10 lg:h-10" />
        </motion.a>

        <div className="mt-16 grid sm:grid-cols-3 gap-10 border-t border-crema/15 pt-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crema/50 mb-3">
              WhatsApp
            </p>
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="text-2xl hover:text-fuchsiabrand transition-colors duration-300"
            >
              347 3275955
            </a>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crema/50 mb-3">
              Instagram
            </p>
            <a
              href="https://instagram.com/alinacossali.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="text-2xl inline-flex items-center gap-2 hover:text-fuchsiabrand transition-colors duration-300"
            >
              <Instagram size={22} /> alinacossali.it
            </a>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crema/50 mb-3">
              Zona
            </p>
            <p className="text-2xl">Bergamo e Lombardia</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <button
            onClick={scrollToHero}
            data-testid="footer-cta"
            className="bg-fuchsiabrand text-white px-7 py-4 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-magentadark transition-colors duration-300"
          >
            Richiedi informazioni
          </button>
          <div className="text-left sm:text-right">
            <p className="font-display text-xl">Alina Cossali</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-crema/40 mt-1">
              Consulente d'Immagine · © {new Date().getFullYear()}
            </p>
          </div>
        </div>

        {/* Dati fiscali (obbligo di legge) + link policy */}
        <div className="mt-12 pt-8 border-t border-crema/15" data-testid="footer-legal">
          <p className="font-mono text-[11px] tracking-[0.08em] text-crema/40 leading-relaxed">
            Ditta Individuale Alina Cossali · P.IVA 04494520168 · C.F. CSSLNA86C56C800I ·
            Sede: Via Pos Palì 2, 24020 Parre (BG)
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
            <PrivacyDialog>
              <button
                type="button"
                data-testid="footer-privacy-link"
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-crema/50 hover:text-fuchsiabrand underline underline-offset-4 transition-colors duration-300"
              >
                Privacy Policy
              </button>
            </PrivacyDialog>
            <CookiePolicyDialog>
              <button
                type="button"
                data-testid="footer-cookie-link"
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-crema/50 hover:text-fuchsiabrand underline underline-offset-4 transition-colors duration-300"
              >
                Cookie Policy
              </button>
            </CookiePolicyDialog>
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.removeItem(CONSENT_KEY);
                } catch (_) {}
                window.dispatchEvent(new Event("cookie-consent-reset"));
              }}
              data-testid="footer-cookie-prefs"
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-crema/50 hover:text-fuchsiabrand underline underline-offset-4 transition-colors duration-300"
            >
              Preferenze cookie
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
